'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import AllsioMark from '@/app/components/AllsioMark'

interface Branding {
  platformName: string
  logoUrl: string | null
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [branding, setBranding] = useState<Branding>({ platformName: 'Allsio', logoUrl: null })

  useEffect(() => {
    fetch('/api/settings/branding')
      .then(r => r.json())
      .then(data => setBranding(data))
      .catch(() => {})
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (res.ok) {
        setSubmitted(true)
      } else {
        const data = await res.json()
        setError(data.error || 'Something went wrong')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--brand-bg-primary)' }}
    >
      <div className="w-full max-w-md">
        <div
          className="p-8 shadow-brand-strong"
          style={{
            background: 'var(--brand-bg-surface)',
            border: '1px solid var(--brand-border)',
            borderRadius: 'var(--brand-radius)',
          }}
        >
          <div className="mb-8 text-center">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" className="h-12 mx-auto mb-4 object-contain" />
            ) : (
              <div className="flex justify-center mb-4">
                <AllsioMark size={52} />
              </div>
            )}
            <h1
              className="text-2xl font-bold tracking-widest uppercase"
              style={{ color: 'var(--brand-accent)', fontFamily: 'var(--font-syncopate, sans-serif)' }}
            >
              {branding.platformName}
            </h1>
            <p className="mt-2 text-sm" style={{ color: 'var(--brand-text-muted)' }}>Reset your password</p>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div
                className="p-4 text-sm"
                style={{
                  background: 'var(--brand-bg-elevated)',
                  border: '1px solid var(--brand-border)',
                  color: 'var(--brand-text-muted)',
                  borderRadius: 'var(--brand-radius)',
                }}
              >
                If an account exists for <strong style={{ color: 'var(--brand-accent)' }}>{email}</strong>, you will receive a password reset link shortly.
              </div>
              <Link
                href="/login"
                className="block text-center text-sm transition-colors"
                style={{ color: 'var(--brand-text-subtle)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-accent)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--brand-text-muted)' }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 focus:outline-none transition-colors"
                  style={{
                    background: 'var(--brand-bg-elevated)',
                    border: '1px solid var(--brand-border)',
                    color: 'var(--brand-text-primary)',
                    borderRadius: 'var(--brand-radius)',
                  }}
                  onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--brand-border)')}
                  placeholder="admin@example.com"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 font-bold text-sm tracking-widest uppercase disabled:opacity-50 transition-opacity"
                style={{
                  background: 'var(--brand-accent)',
                  color: '#0d0d0d',
                  borderRadius: 'var(--brand-radius)',
                  fontFamily: 'var(--font-syncopate, sans-serif)',
                }}
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>

              <Link
                href="/login"
                className="block text-center text-sm transition-colors"
                style={{ color: 'var(--brand-text-subtle)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-accent)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
